// Creating a project: the details forms (the New Project dialog and the first
// project form on the project list) collect name, base prompt and region, then
// the plan decides what happens. Free creates the project right away; Standard
// and Premium go to checkout, and the billing ticket creates the project once
// the payment succeeds (see createSubscription in subscription/subscription.ts).
import type { Router } from "vue-router";
import { type ServiceFromServer, createService } from "@/code/service";
import { addServiceToList } from "@/views/project-list";
import { createSubscription } from "@/views/subscription/subscription";

export type NewProjectPlan = "free" | "standard" | "premium";

export const NEW_PROJECT_PLANS: { plan: NewProjectPlan; label: string }[] = [
    { plan: "free", label: "Free" },
    { plan: "standard", label: "Standard" },
    { plan: "premium", label: "Premium" },
];

export const planLabel = (plan: NewProjectPlan | "") => NEW_PROJECT_PLANS.find((p) => p.plan === plan)?.label || "";

/**
 * The kind of project this dashboard creates. createService sends it to
 * /create-service; a paid project carries it through checkout, so the billing
 * ticket creates the same project: the chat behind a login, signup off, and the
 * hosted chat page in place.
 */
export const NEW_PROJECT_SPEC = "BunnyQuery";

/**
 * How much of the base prompt a checkout can carry: the payment provider keeps
 * at most 500 characters in one metadata value. A longer prompt is created cut
 * to this length and completed once the project exists (completeNewProject).
 */
export const CHECKOUT_DESCRIPTION_MAX = 500;

export interface PendingProject {
    name: string;
    /** The base prompt. */
    description: string;
    /** Empty: the region closest to the visitor, as the picker preselects. */
    region: string;
    /** The id reserved for it at checkout; empty until a checkout was started. */
    reservedId?: string;
}

// The details carried from the form to the plan page, and back from a cancelled
// checkout. Session storage, so a reload of the plan page keeps them; kept in
// memory too for a browser that refuses storage.
const PENDING_KEY = "bunnyquery:new_project";
let pendingInMemory: PendingProject | null = null;

export function savePendingProject(p: PendingProject) {
    pendingInMemory = { ...p };
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingInMemory));
    } catch {
        // storage blocked: memory only
    }
}

export function loadPendingProject(): PendingProject | null {
    if (pendingInMemory) return { ...pendingInMemory };
    try {
        const raw = sessionStorage.getItem(PENDING_KEY);
        const p = raw ? JSON.parse(raw) : null;
        if (p && typeof p.name === "string" && p.name.trim()) {
            pendingInMemory = {
                name: p.name,
                description: typeof p.description === "string" ? p.description : "",
                region: typeof p.region === "string" ? p.region : "",
                reservedId: typeof p.reservedId === "string" ? p.reservedId : "",
            };
            return { ...pendingInMemory };
        }
    } catch {
        // unreadable: no pending project
    }
    return null;
}

export function clearPendingProject() {
    pendingInMemory = null;
    try {
        sessionStorage.removeItem(PENDING_KEY);
    } catch {
        // storage blocked
    }
}

/** The name rules the backend applies. Empty string when the name is fine. */
export function projectNameProblem(name: string): string {
    const n = (name || "").trim();
    if (!n) return "Enter a project name.";
    if (n.length > 256) return "A project name is at most 256 characters.";
    if (/[<>`]/.test(n)) return "Angle brackets and backticks are not allowed";
    return "";
}

/** Where a new project opens: its chat. */
export const newProjectPath = (serviceId: string) => `/my-projects/${serviceId}/agent`;

/** Free: create the project now and open it. */
export async function createFreeProject(p: PendingProject, router: Router) {
    const params: { name: string; description?: string; region?: string } = { name: p.name.trim() };
    if (p.description.trim()) params.description = p.description.trim();
    if (p.region) params.region = p.region;

    const service = (await createService(params)) as ServiceFromServer;
    clearPendingProject();
    try {
        await addServiceToList([service]);
    } catch {
        // The project page pulls it by id when the list could not take it.
    }
    router.push(newProjectPath(service.service));
}

/**
 * Standard or Premium: leave for checkout. The page stays on its loading state
 * until the browser navigates away; a failed session start resolves without
 * navigating (createSubscription shows the reason).
 */
export async function checkoutNewProject(plan: "standard" | "premium", p: PendingProject) {
    savePendingProject(p);
    await createSubscription({
        ticket_id: plan,
        new_project: {
            name: p.name.trim(),
            description: p.description.trim(),
            region: p.region,
            spec: NEW_PROJECT_SPEC,
        },
        // Remembered with the details, so the page that waits for the project
        // knows these details are that project's.
        onReserved: (reservedId) => savePendingProject({ ...p, reservedId }),
    });
}

/** What a details form does on submit: the plan page, or the preselected plan. */
export async function continueNewProject(p: PendingProject, plan: NewProjectPlan | "", router: Router) {
    savePendingProject(p);
    if (plan === "free") return createFreeProject(p, router);
    if (plan === "standard" || plan === "premium") return checkoutNewProject(plan, p);
    router.push({ name: "create" });
}

/**
 * A project a checkout created has appeared (service/main.vue). Its base prompt
 * came through the checkout cut to CHECKOUT_DESCRIPTION_MAX; when the one typed
 * was longer, write the whole prompt now. Answers false when that write failed,
 * so the caller can say the prompt is incomplete. The saved details are cleared
 * either way.
 */
export async function completeNewProject(
    serviceId: string,
    service: { setServiceOption: (params: { description: string }) => Promise<any>; service?: { description?: string } },
): Promise<boolean> {
    const pending = loadPendingProject();
    clearPendingProject();
    const full = pending?.reservedId === serviceId ? pending.description.trim() : "";
    if (full.length <= CHECKOUT_DESCRIPTION_MAX) return true;
    try {
        await service.setServiceOption({ description: full });
        if (service.service) service.service.description = full;
        return true;
    } catch (err) {
        console.warn("[new-project] could not complete the base prompt", err);
        return false;
    }
}
